import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  Slider,
  Typography,
  Stack,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";

const VideoPlayerModal = ({ open, onClose, videoUrl, videoTitle }) => {
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const videoRef = useRef(null);
  const progressBarRef = useRef(null);

  // Handle play/pause
  const togglePlay = () => {
    if (videoRef.current) {
      if (playing) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setPlaying(!playing);
    }
  };

  // Auto-play when modal opens
  useEffect(() => {
    if (open && videoRef.current) {
      // Small timeout to ensure video is loaded in the DOM
      const timer = setTimeout(() => {
        videoRef.current
          .play()
          .then(() => setPlaying(true))
          .catch((error) => {
            console.error("Auto-play failed:", error);
            setPlaying(false);
          });
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [open, videoUrl]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setPlaying(false);
      setCurrentTime(0);
      setDuration(0);
    }
  }, [open]);

  // Handle time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      if (duration === 0 && videoRef.current.duration) {
        setDuration(videoRef.current.duration);
      }
    }
  };

  // Handle seeking
  const handleSeek = (_, newValue) => {
    if (videoRef.current) {
      videoRef.current.currentTime = newValue;
      setCurrentTime(newValue);
    }
  };

  // Handle volume change
  const handleVolumeChange = (_, newValue) => {
    if (videoRef.current) {
      videoRef.current.volume = newValue;
      setVolume(newValue);
      setMuted(newValue === 0);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !muted;
      setMuted(!muted);
      if (muted) {
        // If currently muted, restore previous volume
        videoRef.current.volume = volume === 0 ? 1 : volume;
        setVolume(volume === 0 ? 1 : volume);
      } else {
        // Keep track of volume before muting
        setVolume(videoRef.current.volume);
        videoRef.current.volume = 0;
      }
    }
  };

  // Enter fullscreen
  const enterFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if (videoRef.current.webkitRequestFullscreen) {
        videoRef.current.webkitRequestFullscreen();
      } else if (videoRef.current.msRequestFullscreen) {
        videoRef.current.msRequestFullscreen();
      }
    }
  };

  // Format time in MM:SS
  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "#000",
          color: "#fff",
          borderRadius: "4px",
          overflow: "hidden",
        },
      }}
    >
      <DialogContent sx={{ p: 0, position: "relative", overflow: "hidden" }}>
        {/* Close button */}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            zIndex: 10,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.7)",
            },
          }}
        >
          <CloseIcon />
        </IconButton>

        {/* Video player */}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            sx={{
              width: "100%",
              position: "relative",
              paddingTop: "56.25%", // 16:9 aspect ratio
            }}
          >
            <video
              ref={videoRef}
              src={videoUrl}
              onClick={togglePlay}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => setPlaying(false)}
              onLoadedMetadata={(e) => setDuration(e.target.duration)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
                backgroundColor: "#000",
              }}
            />
          </Box>

          {/* Controls overlay */}
          <Box
            sx={{
              p: 1,
              backgroundColor: "#111",
              borderTop: "1px solid #222",
            }}
          >
            {/* Title */}
            <Typography variant="body2" sx={{ mb: 1, color: "#ccc" }}>
              {videoTitle || "Video Player"}
            </Typography>

            {/* Progress bar */}
            <Box sx={{ mb: 1 }}>
              <Slider
                ref={progressBarRef}
                value={currentTime}
                min={0}
                max={duration || 100}
                onChange={handleSeek}
                aria-label="Progress"
                sx={{
                  color: "#4691E1",
                  height: 4,
                  "& .MuiSlider-thumb": {
                    width: 12,
                    height: 12,
                    transition: "0.3s cubic-bezier(.47,1.64,.41,.8)",
                    "&:hover, &.Mui-focusVisible": {
                      boxShadow: "0px 0px 0px 8px rgba(70, 145, 225, 0.16)",
                    },
                    "&.Mui-active": {
                      width: 16,
                      height: 16,
                    },
                  },
                  "& .MuiSlider-rail": {
                    opacity: 0.28,
                  },
                }}
              />
            </Box>

            {/* Controls */}
            <Stack direction="row" spacing={1} alignItems="center">
              {/* Play/Pause */}
              <IconButton
                onClick={togglePlay}
                sx={{ color: "white" }}
                aria-label={playing ? "pause" : "play"}
              >
                {playing ? <PauseIcon /> : <PlayArrowIcon />}
              </IconButton>

              {/* Time display */}
              <Typography variant="caption" sx={{ color: "#aaa", ml: 1 }}>
                {formatTime(currentTime)} / {formatTime(duration)}
              </Typography>

              <Box sx={{ flexGrow: 1 }} />

              {/* Volume */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ width: 120 }}
              >
                <IconButton
                  onClick={toggleMute}
                  sx={{ color: "white" }}
                  aria-label={muted ? "unmute" : "mute"}
                >
                  {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
                </IconButton>
                <Slider
                  value={muted ? 0 : volume}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={handleVolumeChange}
                  aria-label="Volume"
                  sx={{
                    color: "#4691E1",
                    "& .MuiSlider-rail": {
                      opacity: 0.28,
                    },
                  }}
                />
              </Stack>

              {/* Fullscreen */}
              <IconButton
                onClick={enterFullscreen}
                sx={{ color: "white" }}
                aria-label="fullscreen"
              >
                <FullscreenIcon />
              </IconButton>
            </Stack>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default VideoPlayerModal;
